import classnames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { pickDomAttrs } from '../_util/utils';
import { useTheme } from '../hooks/useThemeSwitch';
import { getColorConfig } from './share';

// import dayjs from 'dayjs';
// import { generateRandomNum } from '../_util/utils';

// 引入 echarts 核心模块，核心模块提供了 echarts 使用必须要的接口。
import * as echarts from 'echarts/core';
// 引入折线图图表，图表后缀都为 Chart
import { LineChart } from 'echarts/charts';
// 引入直角坐标系，数据集，内置数据转换器组件，组件后缀都为 Component
import { DatasetComponent, GridComponent, TransformComponent } from 'echarts/components';
// 标签自动布局，全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
// 引入 Canvas 渲染器，注意引入 CanvasRenderer 或者 SVGRenderer 是必须的一步
import { useCreation, useThrottleFn } from 'ahooks';
import { CanvasRenderer } from 'echarts/renderers';

// 注册必须的组件
import { jsx as _jsx } from "react/jsx-runtime";
echarts.use([LineChart, LabelLayout, UniversalTransition, CanvasRenderer, GridComponent, DatasetComponent, TransformComponent]);

// const mockData: any[] = [];
// const mockDataLength = 200;

// for (let i = 0; i < mockDataLength; i++) {
//   mockData.push({
//     date: dayjs().startOf('day').subtract(1, 'year').add(i, 'hour').toJSON(),
//     in: generateRandomNum({
//       min: 10000,
//       max: 12000,
//     }),
//     out: generateRandomNum({
//       min: 2000,
//       max: 6000,
//     }),
//   });
// }

const ChartMain = props => {
  const {
    xAxisKey,
    data = [],
    series,
    markLine,
    baseClassName,
    prefixClass
  } = props;
  const [_, colors] = useTheme();
  const containerRef = useRef(null);
  const chart = useRef();
  const resizeObserver = useRef();
  const [chartReady, setChartReady] = useState(false);
  const throttleResize = useThrottleFn(() => {
    var _chart$current;
    (_chart$current = chart.current) === null || _chart$current === void 0 || _chart$current.resize();
  }, {
    wait: 100
  });
  const setChartOption = opt => {
    var _chart$current2;
    (_chart$current2 = chart.current) === null || _chart$current2 === void 0 || _chart$current2.setOption(opt, {
      lazyUpdate: true // 开启延迟更新，否则有很大概率报错
    });
  };
  useLayoutEffect(() => {
    setTimeout(() => {
      // 创建 chart 实例
      if (!chartReady) {
        chart.current = echarts.init(containerRef.current, null, {
          locale: 'EN'
        });
      }
      // chart 默认设置（不设置默认 data.source 可能会报错）
      setChartOption({
        dataset: {
          source: []
        },
        series: []
      });

      // 监听大小变化
      resizeObserver.current = new ResizeObserver(entries => {
        entries.forEach(throttleResize.run);
      });
      resizeObserver.current.observe(containerRef.current);

      // 初始化完成
      setChartReady(true);
    }, 0);
    return () => {
      var _chart$current3, _resizeObserver$curre;
      (_chart$current3 = chart.current) === null || _chart$current3 === void 0 || _chart$current3.dispose();
      (_resizeObserver$curre = resizeObserver.current) === null || _resizeObserver$curre === void 0 || _resizeObserver$curre.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    if (!chartReady) {
      return;
    }
    setChartOption({
      dataset: {
        source: data
      }
    });
  }, [chartReady, data]);

  // 配置 color, grid, xAxis, yAxis
  useLayoutEffect(() => {
    if (!chartReady) {
      return;
    }
    setChartOption({
      color: getColorConfig({
        prefixClass,
        colors
      }),
      grid: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        splitLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        splitLine: {
          show: false
        }
      }
    });
  }, [chartReady, colors]);

  // 设置 series
  useLayoutEffect(() => {
    if (!chartReady || Object.keys(colors).length === 0) {
      return;
    }
    const chartSeries = series.flatMap(item => {
      var _item$smooth;
      const color = item.color ? colors["--".concat(prefixClass, "-").concat(item.color, "-senior-color")] : undefined;
      return {
        name: item.name,
        type: 'line',
        itemStyle: {
          color
        },
        lineStyle: {
          width: 1,
          type: 'solid'
        },
        symbolSize: 0,
        // 标记点大小
        showSymbol: false,
        // false时只有在hover下显示标记点
        smooth: (_item$smooth = item.smooth) !== null && _item$smooth !== void 0 ? _item$smooth : false // 开启圆滑曲线会相对较卡，建议关闭
      };
    });
    if (markLine) {
      chartSeries.push({
        data: [],
        name: 'markline',
        type: 'line',
        symbol: 'none',
        markLine: {
          label: {
            show: false
          },
          lineStyle: {
            color: markLine.color ? colors["--".concat(prefixClass, "-").concat(markLine.color, "-senior-color")] : colors["--".concat(prefixClass, "-onSurface-fontHigh-color")],
            width: 1,
            type: 'dashed'
          },
          silent: true,
          symbol: 'none',
          data: [{
            yAxis: markLine.value,
            name: 'markline'
          }]
        }
      });
    }
    setChartOption({
      dataset: {
        dimensions: [xAxisKey].concat(series.map(item => item.key))
      },
      series: chartSeries,
      legend: {
        show: false,
        selected: series.reduce((acc, cur) => {
          var _cur$show;
          acc[cur.name] = (_cur$show = cur.show) !== null && _cur$show !== void 0 ? _cur$show : true;
          return acc;
        }, {})
      }
    });
  }, [chartReady, colors, xAxisKey, series, markLine]);
  return /*#__PURE__*/_jsx("div", {
    ref: containerRef,
    className: "".concat(baseClassName, "--container")
  });
};
const Chart = props => {
  const {
    data,
    width,
    height,
    prefixClass = 'zen-design',
    //TODO 写provider时统一改掉
    className,
    style,
    ...restProps
  } = props;
  const baseClassName = "".concat(prefixClass, "-minimalist-line-chart");

  // const [data, setData] = useState<any[]>(mockData);
  // useLayoutEffect(() => {
  //   window.setTimeout(() => {
  //     setData(mockData);
  //   }, 5000);
  //   window.setTimeout(() => {
  //     setData(mockData.slice(0, 60));
  //   }, 10000);
  //   window.setTimeout(() => {
  //     setData(mockData.slice(0, 80));
  //   }, 15000);
  // }, []);

  const series = useCreation(() => {
    var _props$series;
    return (_props$series = props.series) !== null && _props$series !== void 0 ? _props$series : [];
  }, [JSON.stringify(props.series)]);
  const attrs = pickDomAttrs(restProps);
  return /*#__PURE__*/_jsx("div", {
    ...attrs.data,
    className: classnames(baseClassName, className),
    style: {
      ...style,
      width,
      height
    },
    children: !(data !== null && data !== void 0 && data.length) ? /*#__PURE__*/_jsx("div", {
      className: "".concat(baseClassName, "--empty")
    }) : /*#__PURE__*/_jsx(ChartMain, {
      ...props,
      data: data,
      series: series,
      prefixClass: prefixClass,
      baseClassName: baseClassName
    })
  });
};
export default Chart;